<script>
// eslint-disable-next-line import/order
import { createMeta } from './meta.js';

export default {
  name: 'Checkout',
  meta() {
    return createMeta(this);
  },
};
</script>
<script setup>
import {
  ref,
  inject,
  provide,
  computed,
  onMounted,
  nextTick,
  onUnmounted,
  watchEffect,
} from 'vue';
import {
  getCheckoutOption,
  getGoCheckout,
  getEWalletsPaymentId,
} from 'src/api/checkout.js';
import { getCartDetailsData } from 'src/api/cart.js';
import {
  availableShippingMethods,
  changeShippingMethod,
} from 'src/api/checkout';
import { debounce } from 'quasar';
import Products from 'src/pages/full_dine_in/components/Products.vue';
import ErrDialog from 'src/components/ErrorDialog.vue';
import MInput from 'src/components/MInput/index';
import MButton from 'src/components/MButton/index.vue';
import { MToast } from 'src/components/MToast';

import {
  setCommit,
  scrollToActiveEl,
  addShopeePayPromo,
} from 'src/utils/common';
import { use18n, useIns, useStore } from 'src/utils/v2_helper/common.js';
import { useRoute, useRouter } from 'src/utils/v2_helper/router/globals.js';
import { showDialog } from 'src/utils/error_config';
import { useLoading } from 'src/utils/hooks/loading';
import beforeCheckoutDialog from 'components/beforeCheckoutDialog/index.vue';
import RecommendProduct from 'src/components/recommend/RecommendProduct.vue';
import { getUserInfo, guestBuyerMerge, guestBuyerUpdate, guestLogin } from 'src/api/login';
import { checkEmailOptional, checkPhone, isBluPhoneValid, handleBuyerInfoFailure, handleCheckoutError } from './utils';
import { transformProductData } from '../full_dine_in/util';
import Paying from './components/Paying.vue';
import Payment from './components/Payment/index.vue';
import ApartmentForm from './components/ApartmentForm/ApartmentForm.vue';
import DeliveryMethod from './components/DeliveryMethod.vue';
import FreeProduct from './components/FreeProduct.vue';
import Packaging from './components/Packaging.vue';
import UseVoucher from './components/useVoucher/index.vue';
import UserInfo from './components/UserInfo.vue';
import { isPaymentAvailable, isQRIS } from './components/Payment/config.js';
import {
  ADD_SUCCESS_RECOMMEND,
  CHECKOUT_OPTION_LOADED,
  MAX_NOTE_LENGTH,
} from '../../utils/config.js';

const { $t } = use18n()
const app = useIns();
const router = useRouter();
const route = useRoute();
const store = useStore();

const reload = inject('reload');
app.reload = reload;
const { loading, hide } = useLoading();

const userRef = ref();
const paymentRef = ref();
const voucherRef = ref();

const checkoutState = ref('1');
const errorReason = ref('');
const checkoutOption = ref({});
const paymentParams = ref({
  payId: '',
  name: 'QRIS_ONLINE',
  phoneNumber: '',
  bluPhoneNumber: '',
  manual: true,
  iconUrl: '',
});
const comment = ref('');
const showOneType = ref('');
const paymentMethods = ref([]);
const payLock = ref(false);
const paying = ref(false);
const vouchers = ref([]);
const loaded = ref(false);
const shipping = ref({
  price: 0,
  list: [],
  cur: null,
});
const apartmentForm = ref({
  valid: false,
  apartmentAddress: '',
  apartmentAddressDetail: '',
});

const Menu = computed(() => store.state.Menu);
const contactMechId = computed(() => Menu.value.addressInfo?.contactMechId);
const scheduled = computed(() => Menu.value.scheduled);
const scheduledTime = computed(() => Menu.value.scheduledTime);
const scheduledEndTime = computed(() => Menu.value.scheduledEndTime);
const checkData = computed(() =>
  transformProductData(checkoutOption.value.cart || {}),
);
const isCorrectOvo = computed(() => {
  const phone = paymentParams.value.phoneNumber;
  return (
    phone !== '+62' &&
    new RegExp('^(\\+62).*$').test(phone) &&
    phone.length > 10
  );
});

// AI-assisted 2026-09 START: Refresh server totals when packaging changes.
function changePackaging(spunbondEnabled) {
  if (paying.value) return;
  return refreshCheckoutOptions({ reload: false, spunbondEnabled });
}
// AI-assisted 2026-09 END
// AI-assisted 2026-09 START: Blu by BCA checkout validation
const isCorrectBlu = computed(() => {
  const phone = paymentParams.value.bluPhoneNumber;
  return isBluPhoneValid(phone);
});
// AI-assisted 2026-09 END: Blu by BCA checkout validation
const selectedPaymentAvailable = computed(() =>
  allowPaymentMethods.value.some(
    (item) => item.id === paymentParams.value.payId && isPaymentAvailable(item, grandTotal.value),
  ),
);
const isPaymentSelected = computed(() => Boolean(paymentParams.value?.payId));
const isCheckout = computed(() => {
  if (!grandTotal.value) return false;
  if (paying.value) return false;
  // 支付方式判断
  if (isPaymentSelected.value && !selectedPaymentAvailable.value) return false;
  // 支付锁，虚拟支付使用，判断
  if (payLock.value) return false;
  // dine 各种业务类型判断
  if (app.isDine) {
    const { invalidTable } = userRef.value || {};
    if (app.isApartment && !apartmentForm.value.valid) return false;
    if (app.isRoomService && invalidTable) return false;
    if (app.isNormalDineIn && invalidTable) return false;
  }
  // delivery模式判断
  if (app.isDelivery && !userRef.value?.deliveryValid) return false;
  // 预订单判断
  if (scheduled.value && !scheduledTime.value) return false;
  // ovo号码判断
  if (paymentParams.value.name === 'OVO' && !isCorrectOvo.value) return false;
  if (paymentParams.value.payId === 'IFORTE_BLU' && !isCorrectBlu.value) return false;
  // 用户信息判断（已登录用户进行该校验）
  if (!app.userInfo.guestUser && !userRef.value?.userValid) return false;
  // 补充用户信息判断（游客进行该校验）
  if (app.userInfo.guestUser && !userRef.value?.fillFormValid) return false;
  return true;
});
const freeProducts = computed(
  () => checkoutOption.value.cart?.cartGiftItems || [],
);
const grandTotal = computed(() => checkoutOption.value?.cart?.grandTotal || 0);
// 最大在线支付金额限制
const maxOnlinePayAmount = computed(() => app.storeInfo?.maxOnlinePayAmount || 0)
// 是否超过最大在线支付金额
const overMaxOnlinePayAmount = computed(() => {
  // -1表示无限制
  if (maxOnlinePayAmount.value === -1) return false
  return grandTotal.value > maxOnlinePayAmount.value
})
// 当前允许的支付方式
const allowPaymentMethods = computed(() => {
  if (overMaxOnlinePayAmount.value) {
    return paymentMethods.value.filter(item => item.type !== 'e-wallet')
  }
  return paymentMethods.value
})

// ==== funcs ====
// 获取可用物流列表
function fetchShippings() {
  return new Promise((resolve, reject) => {
    if (!contactMechId.value) {
      reject('NOT_CONTACT');
      return userRef.value.onCheckFaild();
    }
    availableShippingMethods({ contactMechId: contactMechId.value })
      .then((res = {}) => {
        const { code, data } = res;
        if (code === 200) {
          const shipTypes = data ?? [];
          const list = shipTypes
            .filter(Boolean)
            .map((it) => it.rates[0])
            .filter(Boolean);
          shipping.value.list = list;
          if (list.length === 0) {
            userRef.value.onCheckFaild();
            reject();
          } else {
            resolve([...list]);
          }
        } else {
          reject();
        }
      })
      .catch(() => reject());
  });
}
// AI-assisted 2026-09 START: Update shipping, then refresh server packaging totals.
async function useShipping(id) {
  loading();
  try {
    let list = await fetchShippings();
    list = list.sort((it, he) => it.cost - he.cost);
    const findOne = list.find((it) => it.id === id);
    const shippingMethod = id && findOne ? findOne.id : list[0].id;
    const params = { shippingMethod, contactMechId: contactMechId.value };
    const res = await changeShippingMethod(params);
    if (!res.success && res.code !== 200) throw res;
    shipping.value.cur = findOne || list[0];
    await refreshCheckoutOptions({ reload: false });
  } finally {
    hide();
  }
}
// AI-assisted 2026-09 END
// 支付三板斧
async function payParams() {
  const { payId, phoneNumber } = paymentParams.value;
  if (payId === 'OVO') {
    const valid = await checkPhone(phoneNumber, app);
    if (!valid) return Promise.reject('CHECK_PHONE_FAILED');
  }
  try {
    const params = { ...paymentParams.value, paymentMethodType: payId };
    const res = await getEWalletsPaymentId(params);
    if (!res.success && (payId === 'IFORTE_BLU' || res.data?.errorKey)) throw res;
    if (!res.success) return Promise.reject('NE');
    const time = scheduled.value ? +scheduledTime.value : 0;
    const endTime = scheduled.value ? +scheduledEndTime.value : 0;
    const { valid: _, ...fields } = apartmentForm.value;
    const buyerInfo = app.userInfo.guestUser
      ? userRef.value.getFillInfo()
      : userRef.value.getUser();
    const buyerPhone = app.userInfo.guestUser ? buyerInfo.phone : app.userInfo.phone;
    const checkoutParams = {
      is_gift: false,
      may_split: false,
      shipping_contact_mech_id: checkoutOption.value.contactMechId,
      shipping_method: checkoutOption.value.shippingMethodId,
      checkOutPaymentId: res.data,
      serviceType: app.accessStatus,
      comment: setCommit(comment.value),
      scheduledDate: time || 0,
      scheduledEndDate: endTime || 0,
      buyerName: buyerInfo.nickName || '',
      buyerEmail: buyerInfo.email || '',
      buyerPhone: String(buyerPhone || '').replace(/^\+62/, ''),
    };
    if (vouchers.value?.[0]) {
      checkoutParams.productPromoCodeId = vouchers.value[0].promoCode;
    }
    if (app.isDelivery) {
      checkoutParams.shipping_contact_mech_id = contactMechId.value;
      checkoutParams.shipping_method = shipping.value.cur?.id;
    }
    if (app.isDine) {
      checkoutParams.tableNumber = userRef.value.getTable();
      if (app.isApartment) {
        // apartment 拼接tableNumber传递
        checkoutParams.tableNumber = `${fields.apartmentAddress} ${fields.apartmentAddressDetail}`;
      }
    }
    return checkoutParams;
  } catch (error) {
    console.log(error);
    return Promise.reject(error);
  }
}
// Submit checkout and route using the returned payment channel before selection-based fallbacks.
async function pay(params) {
  try {
    const res = await getGoCheckout(params);
    const { data, code, message, success } = res;
    // AI-assisted 2026-09 START: Stop after showing the known checkout payment error.
    if (!success && message === 'Kesalahan pembayaran') {
      paying.value = false;
      showDialog(app, 500, { title: $t('checkout.paymentError') });
      return;
    }
    // AI-assisted 2026-09 END
    // AI-assisted 2026-09 START: Preserve structured quote errors before generic code handling.
    if (!success) {
      paying.value = false;
      if (handleBuyerInfoFailure(res, app, userRef.value)) return;
      const checkoutError = handleCheckoutError(res, app);
      if (checkoutError) {
        if (checkoutError.refresh) await refreshCheckoutOptions({ reload: false });
        return;
      }
    }
    // AI-assisted 2026-09 END
    if (success) {
      // 如果是游客，则需要清除游客信息
      if (app.userInfo.guestUser) {
        await store.dispatch('Menu/resetUser');
      }
      // AI-assisted 2026-09 START: Use the actual Blu attempt returned by checkout.
      if (data?.paymentMethod === 'IFORTE_BLU') {
        if (!data.orderDetail?.orderId) {
          paying.value = false;
          MToast({ msg: $t('waiting.bluMissingOrder') });
          return;
        }
        return router.push({
          path: `/${app.storeUrl}/blu-waiting`,
          query: { orderId: data.orderDetail.orderId },
        });
      }
      // AI-assisted 2026-09 END
      if (isQRIS(paymentParams.value)) {
        return router.push({
          name: 'qris-waiting',
          query: { orderId: data?.orderDetail.orderId },
        });
      }
      // Virtual Pay 判断
      if (paymentRef.value.isVirtual) {
        router.push({
          path: `/${app.storeUrl}/virtual-account`,
          query: {
            orderId: data?.orderDetail.orderId,
          },
        });
      } else if (paymentParams.value.name === 'DANA') {
        router.push({
          path: `/${app.storeUrl}/dana-waiting`,
          query: {
            orderId: data?.orderDetail.orderId,
          },
        });
      } else if (
        ['MIDE_SHOPEEPAY', 'XE_SHOPEEPAY'].indexOf(paymentParams.value.payId) >=
        0
      ) {
        store.dispatch('Menu/setOrderId', data?.orderDetail.orderId);
        router.push({
          path: `/${app.storeUrl}/shopee-waiting`,
          query: {
            orderId: data?.orderDetail.orderId,
          },
        });
      } else if (
        paymentParams.value.payId === 'XE_OVO' ||
        paymentParams.value.payId === 'OVO'
      ) {
        router.push({
          path: `/${app.storeUrl}/ovo-waiting`,
          query: {
            orderId: data?.orderDetail.orderId,
          },
        });
      } else if (paymentParams.value.payId === 'MIDE_GOPAY') {
        store.dispatch('Menu/setOrderId', data?.orderDetail.orderId);
        // if (data?.checkout_url) openUrl(data?.checkout_url);
        router.push({
          path: `/${app.storeUrl}/gopay-waiting`,
          query: {
            orderId: data?.orderDetail.orderId,
          },
        });
      } else if (paymentParams.value.payId === 'CASH') {
        router.push({
          path: `/${app.storeUrl}/result/success`,
          query: {
            orderId: data?.orderDetail.orderId,
            paymentMethodType: paymentParams.value.payId,
          },
        });
      }
    } else if (code === 803) {
      voucherRef.value.toFaild(); // checkout 时 voucher 是失效的
      showDialog(app, code);
    } else if (code === 815) {
      showDialog(app, code, {}, { secondTile: message });
    } else if (code === 710) {
      showDialog(app, code)
    } else {
      showDialog(app, code);
    }
  } catch (error) {
    // AI-assisted 2026-09 START: Cover the same checkout error when HTTP rejects.
    if (error?.message === 'Kesalahan pembayaran') {
      paying.value = false;
      showDialog(app, 500, { title: $t('checkout.paymentError') });
      return;
    }
    // AI-assisted 2026-09 END
    console.log(error);
    return Promise.reject(error);
  }
}
const onPay = debounce(async () => {
  if (!isPaymentSelected.value) {
    MToast({ msg: $t('checkout.choosePaymentTip') });
    return;
  }
  if (!isCheckout.value || paying.value) return window.scrollTo(0, 0);
  if (scheduled.value) {
    const isOK = userRef.value.onCheckTime();
    if (!isOK) return showDialog(app, 8001);
  }
  // 判断支付方式是否为线上支付（线上支付需弹窗确认）
  if (paymentParams.value.type === 'e-wallet') {
    const dialog = app.$q.dialog({
      component: beforeCheckoutDialog,
      parent: app,
      type: '',
      title: $t('checkout.payNowTipTitle'),
      describe: $t('checkout.payNowTipDesc'),
      ok: $t('checkout.payNowTipOk'),
      cancel: $t('checkout.payNowTipCancel'),
    })
      .onOk(() => {})
      .onCancel(paymentProgress)
  } else {
    await paymentProgress()
  }
}, 400);

// AI-assisted 2026-09 START: Prepare payment and handle structured checkout errors.
async function paymentProgress() {
  if (!isCheckout.value || paying.value) return;
  paying.value = true;
  try {
    const res = await store.dispatch('prefetch/prefetchStoreInfo', {
      currentRoute: route,
    });
    // dine pickup 互转
    const { dineIn, pickUp } = store.state.prefetch.storeInfo;
    if (!scheduled.value && !app.isFullDineIn) {
      if (dineIn && !pickUp && app.isPickup) {
        showOneType.value = 'dineIn';
        paying.value = false;
        return;
      }
      if (pickUp && !dineIn && app.isDine) {
        showOneType.value = 'pickUp';
        paying.value = false;
        return;
      }
    }
    // update user & check emailoptinal (用户登录后才会执行该逻辑)
    if (!app.userInfo?.guestUser && (!app.userInfo || !app.userInfo.email || !app.userInfo.nickName)) {
      const user = userRef.value.getUser();
      await checkEmailOptional(user.email, app);
      await store.dispatch('Menu/patchUserInfo', user);
    } else if (app.userInfo?.guestUser) {
      // 游客登录
      const guestUserInfo = { ...userRef.value.getFillInfo() };
      localStorage.setItem('guestUser', JSON.stringify(guestUserInfo));
      guestUserInfo.phone = guestUserInfo.phone ? `+62${guestUserInfo.phone}` : ''
      await guestBuyerUpdate(guestUserInfo);
    }
    const params = await payParams();
    await pay(params);
  } catch (e) {
    paying.value = false;
    if (handleBuyerInfoFailure(e, app, userRef.value)) return;
    const checkoutError = handleCheckoutError(e, app);
    if (checkoutError) {
      if (checkoutError.refresh) await refreshCheckoutOptions({ reload: false });
      return;
    }
    console.log('====ON_PAY_ERROR====', e);
    if (paymentParams.value.payId === 'IFORTE_BLU' && e?.code) showDialog(app, e.code);
    if (e === 'NE') MToast({ msg: 'Network Error' });
  }
}
// AI-assisted 2026-09 END

// 刷新checkout options
// AI-assisted 2026-09 START: Load checkout data directly using the existing refresh flow.
async function refreshCheckoutOptions(options = { reload: true }) {
  if (options.reload) loaded.value = false;
  loading();
  try {
    const params = { serviceType: app.accessStatus, isScheduled: Menu.value.scheduled };
    if (typeof options.spunbondEnabled === 'boolean') params.spunbondEnabled = options.spunbondEnabled;
    const res = await getCheckoutOption(params);
    const { data, code, success } = res;
    loaded.value = success;
    if (!success) {
      if (code === 205) return router.replace({ name: 'login' });
      if (!handleCheckoutError(res, app)) showDialog(app, code);
      return;
    }
    checkoutOption.value = data;
    paymentMethods.value = addShopeePayPromo(app, data.paymentMethods || []);
    shipping.value.price = data.cart?.shippingTotal || 0;
    if (voucherRef.value?.pass) {
      const promo = data.cart?.cartPromos?.[0];
      vouchers.value = promo ? [promo] : [];
    }
    if (!app.guestCheckout && !app.userInfo.guestUser) {
      guestBuyerMerge({ productStoreId: app.storeInfo.id });
    }
  } catch (error) {
    if (!handleCheckoutError(error, app) && error?.code) showDialog(app, error.code);
  } finally {
    hide();
  }
}

const run = () => {
  loading();
  getCartDetailsData().finally(() => {
    hide();
    refreshCheckoutOptions();
  });
};
// AI-assisted 2026-09 END
onMounted(() => {
  nextTick(() => {
    if (!app.logged) {
      router.push(`/${app.storeUrl}/login`);
      store.dispatch('Menu/resetUser');
    } else {
      refreshCheckoutOptions();
    }
  });
  // 监听推荐商品加入成功事件
  app.$bus.$off(ADD_SUCCESS_RECOMMEND, run);
  app.$bus.$on(ADD_SUCCESS_RECOMMEND, run);
});
onUnmounted(() => {
  app.$bus.$off(ADD_SUCCESS_RECOMMEND, run);
});

provide('payment', paymentParams);
provide('vouchers', vouchers);
provide('refresh', refreshCheckoutOptions);
// change payment 提供使用voucher时，会自动选择正确的支付方式
provide('dispatchPayment', (id) => paymentRef.value.change(id, false));
provide('refreshShippingPrice', useShipping);
provide('changeVoucher', (v) => {
  vouchers.value = v;
});
provide('resetState', () => {
  shipping.value.cur = null;
  shipping.value.price = 0;
  shipping.value.list = [];
  // checkoutOption.value = {};
  // voucher.value = null;
});
const pordData = computed(() => checkoutOption.value.cart || {});
provide('prod_data', pordData);
provide('hide_timeline', true);

watchEffect(() => {
  if (loaded.value) app.$bus.$emit(CHECKOUT_OPTION_LOADED);
});
</script>

<template>
  <div class="checkout">
    <div v-if="!loaded && checkData.cart" class="checkout-empty">
      Cart is empty
    </div>
    <div class="checkout-main">
      <section>
        <UserInfo ref="userRef" :checkout-option="checkoutOption" />
      </section>
      <ApartmentForm
        v-if="loaded && isDine && isApartment"
        @change="(v) => (apartmentForm = v)"
      />
      <!--Product & Fee-->
      <Products />
      <!-- AI-assisted 2026-09 START: Quote-backed packaging. -->
      <Packaging
        :spunbond="checkoutOption.spunbond"
        :disabled="paying"
        @change="changePackaging"
      />
      <!-- AI-assisted 2026-09 END -->

      <!--Delivery Info-->
      <section v-if="isDelivery && shipping.cur" class="main-delivery">
        <div class="main-delivery-head">
          {{ $t('checkout.courier') }}
          <DeliveryMethod :shipping.sync="shipping" @use="useShipping" />
        </div>
        <div class="main-delivery-row">
          <img
            v-if="shipping.cur"
            :src="`https://static.bakmigm.co.id/menu/${shipping.cur.name}.png`"
            class="icon"
          />
          <span v-if="shipping.price" class="price">
            {{ $pid(shipping.price) }}
          </span>
          <span v-else class="free">Free</span>
        </div>
      </section>
      <!--Free Product-->
      <section v-if="freeProducts.length" class="main-free_prod">
        <FreeProduct :data="freeProducts" />
      </section>
      <!--Voucher-->
      <RecommendProduct v-if="loaded">
        <div class="recommendation-tile">{{ $t('common.recommendation') }}</div>
      </RecommendProduct>

      <section class="main-voucher">
        <UseVoucher ref="voucherRef" :payments="allowPaymentMethods" />
      </section>
<!--      <section class="main-comment">-->
<!--        <div class="head">-->
<!--          <span class="key">{{ $t('checkout.notes') }}</span>-->
<!--          <span class="count">-->
<!--            {{ comment.length }} / {{ MAX_NOTE_LENGTH }}-->
<!--          </span>-->
<!--        </div>-->
<!--        <MInput-->
<!--          v-model="comment"-->
<!--          :placeholder="$t('checkout.notesHint')"-->
<!--          :max="MAX_NOTE_LENGTH"-->
<!--          @focus="scrollToActiveEl"-->
<!--        />-->
<!--      </section>-->
    </div>
    <div v-if="loaded" class="paynow">
      <div v-show="overMaxOnlinePayAmount" class="paynow-tip">
        {{ $t('checkout.overMaxOnlinePayAmount', {amount: $pid(maxOnlinePayAmount)}) }}
      </div>
      <Payment
        ref="paymentRef"
        :payment-methods="allowPaymentMethods"
        :payment-amount="grandTotal"
        :pay-lock.sync="payLock"
        @changePayment="(v) => Object.assign(paymentParams, { ...v })"
      />
      <MButton
        :key="loaded"
        class="paynow-btn"
        primary
        :disabled="!isCheckout"
        @click="onPay"
      >
        {{ $t('checkout.paynow') }} ·
        {{ $pid(grandTotal) }}
      </MButton>
    </div>
    <ErrDialog :show-one-type="showOneType" />
    <Paying v-model="paying" />
  </div>
</template>
<style lang="scss" scoped>
.recommendation {
  background: #fff;
  &-tile {
    font-weight: bold;
    font-size: 12px;
    margin-bottom: 8px;
  }
}
.checkout {
  height: 100%;
  padding-top: 72px;
  background: $grayBg;
  &-main {
    padding-bottom: 164px;
    background: #fff;
    section + section {
      border-top: solid 1px $border-lg;
    }
    .main-delivery {
      padding: 16px;
      &-head {
        font-size: 12px;
        color: $black;
        font-weight: 800;
        display: flex;
        justify-content: space-between;
      }
      &-row {
        display: flex;
        justify-content: space-between;
        padding-top: 12px;
        .free,
        .price {
          font-weight: bold;
          font-size: 16px;
        }
        .free {
          color: #10b981;
        }
        .icon {
          width: 90px !important;
          height: 24px !important;
        }
      }
    }
    .main-comment {
      padding: 16px;
      font-size: 12px;
      .head {
        display: flex;
        margin-bottom: 8px;
        .key {
          font-weight: bold;
        }
        .count {
          margin-left: auto;
        }
      }
    }
  }
  .paynow {
    position: absolute;
    bottom: 0;
    bottom: $SAFE_SIZE;
    bottom: $SAFE_SIZE1;
    left: 0;
    right: 0;
    padding: 8px 16px 16px;
    border-top: solid 1px $border;
    background: #fff;
    &-tip {
      display: flex;
      padding: 6px;
      border-radius: 8px;
      background: #F3F4F6;
      color: #111827;
      font-size: 11px;
      font-weight: 400;
      line-height: 14px;
    }
    &-btn {
      font-weight: bold;
      width: 100%;
      font-size: 16px;
    }
  }
  &-empty {
    font-size: 20px;
    text-align: center;
    line-height: 200px;
  }
}
</style>
